import { isDesk, type Viewer, type WireNotification } from '@alphadesk/web/contract.ts';
import { PAGES } from '@alphadesk/web/links.ts';
import clsx from 'clsx';
import {
  Bell,
  Boxes,
  CalendarDays,
  ChartColumn,
  CircleHelp,
  FilePlus2,
  House,
  LayoutList,
  MoreHorizontal,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { draftOf } from '../lib/feedback.ts';
import { isHere, navFor, type PageLink } from '../lib/nav.ts';
import { useLinkClick, useRoute } from '../lib/router.ts';
import { useHeight } from '../lib/screen.ts';
import type { ConnectionState } from '../lib/stream.ts';
import { BrokerBanner, BrokerPill } from './Broker.tsx';
import { useFeedback } from './Feedback.tsx';
import { InboxBell, InboxRail } from './Inbox.tsx';
import { InvitePill } from './Invite.tsx';
import { Brand } from './Mark.tsx';
import { menuItem, navLink } from './primitives/controls.tsx';
import { Avatar, Count } from './primitives/marks.tsx';
import { HeaderMenu } from './primitives/panel.tsx';

function TabButton({
  icon,
  label,
  on = false,
  expanded,
  dot,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  on?: boolean;
  expanded: boolean;
  dot?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      // No KEEP: the broker tab's label is the Zerodha account id.
      className={clsx(
        'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-2xs font-medium transition-colors',
        on || expanded ? 'text-ink' : 'text-ink-faint',
      )}
    >
      <span className="relative">
        {icon}
        {dot && (
          <span
            aria-hidden
            className={clsx(
              'absolute -top-0.5 -right-1 size-2 rounded-full ring-2 ring-canvas',
              dot,
            )}
          />
        )}
      </span>
      {label}
    </button>
  );
}

const CONNECTION: Record<ConnectionState, { label: string; dot: string; text: string }> = {
  connecting: { label: 'Connecting', dot: 'bg-warn animate-pulse', text: 'text-ink-muted' },
  live: { label: 'Live', dot: 'bg-ink', text: 'text-ink-muted' },
  offline: { label: 'Reconnecting', dot: 'bg-warn animate-pulse', text: 'text-warn' },
};

/** Always on screen: a stale price that looks live could cost money. */
function Connection({ state }: { state: ConnectionState }) {
  const look = CONNECTION[state];
  return (
    <span
      role="status"
      aria-label={look.label}
      title={look.label}
      className={clsx('flex items-center gap-1.5 text-xs', look.text)}
    >
      <span aria-hidden className={clsx('size-1.5 rounded-full', look.dot)} />
      <span className={clsx(state === 'live' && 'hidden sm:inline')}>{look.label}</span>
    </span>
  );
}

function Nav({ viewer }: { viewer: Viewer }) {
  const { path } = useRoute();
  const onClick = useLinkClick();
  return (
    <nav
      aria-label="Pages"
      className="no-scrollbar hidden min-w-0 items-center gap-0.5 overflow-x-auto sm:flex"
    >
      {navFor(viewer).map((item) => {
        const here = isHere(path, item.href);
        return (
          <a
            key={item.href}
            href={item.href}
            onClick={onClick}
            aria-current={here ? 'page' : undefined}
            className={navLink(here)}
          >
            {item.label}
          </a>
        );
      })}
    </nav>
  );
}

const PAGE_ICON: Record<string, ReactNode> = {
  [PAGES.home]: <House aria-hidden className="size-4" />,
  [PAGES.trades]: <LayoutList aria-hidden className="size-4" />,
  [PAGES.analysts]: <Users aria-hidden className="size-4" />,
  [PAGES.add]: <FilePlus2 aria-hidden className="size-4" />,
  [PAGES.notifications]: <Bell aria-hidden className="size-4" />,
  [PAGES.investments]: <Wallet aria-hidden className="size-4" />,
  [PAGES.pnl]: <ChartColumn aria-hidden className="size-4" />,
  [PAGES.markets]: <CalendarDays aria-hidden className="size-4" />,
  [PAGES.desk]: <Boxes aria-hidden className="size-4" />,
  [PAGES.help]: <CircleHelp aria-hidden className="size-4" />,
};

function PageList({ pages, onGo }: { pages: PageLink[]; onGo: () => void }) {
  const { path } = useRoute();
  const onClick = useLinkClick();
  return (
    <nav aria-label="More pages" className="flex flex-col gap-0.5">
      {pages.map((page) => (
        <a
          key={page.href}
          href={page.href}
          aria-current={isHere(path, page.href) ? 'page' : undefined}
          onClick={(event) => {
            onGo();
            onClick(event);
          }}
          className={menuItem({ here: isHere(path, page.href) })}
        >
          {PAGE_ICON[page.href]}
          {page.label}
        </a>
      ))}
    </nav>
  );
}

/** Settings, broker, Telegram, the look and signing out are the account page's. */
function AccountLink({ viewer }: { viewer: Viewer }) {
  const { path } = useRoute();
  const onClick = useLinkClick();
  const feedback = useFeedback();
  const unsent = !isDesk(viewer) && draftOf(feedback.draft).sendable;
  return (
    <a
      href={PAGES.account}
      onClick={onClick}
      aria-current={isHere(path, PAGES.account) ? 'page' : undefined}
      aria-label={unsent ? 'Your account — unsent note to the admins' : 'Your account'}
      title={viewer.email}
      className="relative hidden rounded-full ring-2 ring-transparent transition-shadow hover:ring-line focus-visible:ring-accent sm:block"
    >
      <Avatar picture={viewer.picture} name={viewer.name} className="size-7 text-xs" />
      {unsent && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-accent ring-2 ring-canvas"
        />
      )}
    </a>
  );
}

function Tab({
  href,
  icon,
  label,
  badge,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  badge?: number;
}) {
  const { path } = useRoute();
  const onClick = useLinkClick();
  const here = isHere(path, href);
  return (
    <a
      href={href}
      onClick={onClick}
      aria-current={here ? 'page' : undefined}
      className={clsx(
        'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-2xs font-medium transition-colors',
        here ? 'text-ink' : 'text-ink-faint',
      )}
    >
      <span className="relative">
        {icon}
        {badge !== undefined && badge > 0 && <Count n={badge} className="-top-1.5 -right-2.5" />}
      </span>
      {label}
    </a>
  );
}

function MoreTab({ pages }: { pages: PageLink[] }) {
  const [open, setOpen] = useState(false);
  const { path } = useRoute();
  const here = pages.some((page) => isHere(path, page.href));
  return (
    <HeaderMenu
      open={open}
      onDismiss={() => setOpen(false)}
      className="flex min-w-0 flex-1"
      trigger={
        <TabButton
          icon={<MoreHorizontal aria-hidden className="size-5" />}
          label="More"
          on={here}
          expanded={open}
          onClick={() => setOpen((current) => !current)}
        />
      }
    >
      <PageList pages={pages} onGo={() => setOpen(false)} />
    </HeaderMenu>
  );
}

/** Home, Trades and the inbox stay a thumb away; the rest is under More. */
const TABBED: readonly string[] = [PAGES.home, PAGES.trades, PAGES.notifications];

function TabBar({ viewer, unread }: { viewer: Viewer; unread: number }) {
  return (
    <nav
      aria-label="Pages"
      className="tab-bar fixed inset-x-0 bottom-0 z-30 border-t border-line sm:hidden"
    >
      <div className="flex h-14 items-stretch">
        <Tab href={PAGES.home} icon={<House aria-hidden className="size-5" />} label="Home" />
        <Tab
          href={PAGES.trades}
          icon={<LayoutList aria-hidden className="size-5" />}
          label="Trades"
        />
        <Tab
          href={PAGES.notifications}
          icon={<Bell aria-hidden className="size-5" />}
          label="Inbox"
          badge={unread}
        />
        <MoreTab pages={navFor(viewer).filter((page) => !TABBED.includes(page.href))} />
        <Tab
          href={PAGES.account}
          icon={<UserRound aria-hidden className="size-5" />}
          label="Account"
        />
      </div>
    </nav>
  );
}

export function Shell({
  viewer,
  connection,
  notifications,
  unread,
  rail,
  banner,
  children,
}: {
  viewer: Viewer;
  connection: ConnectionState;
  notifications: WireNotification[];
  unread: number;
  /** A page's own rail, drawn where the inbox's would be. */
  rail?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
}) {
  // Written to the document, not a wrapper: the viewport's scroll-padding needs the
  // header height too, and cannot see inside this tree.
  const [head, headHeight] = useHeight();
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-head', `${headHeight}px`);
    return () => {
      root.style.removeProperty('--app-head');
    };
  }, [headHeight]);

  const onClick = useLinkClick();
  const { path } = useRoute();

  return (
    <div className="flex min-h-dvh flex-col">
      <header
        ref={head}
        className="sticky top-0 z-30 border-b border-line bg-canvas/90 pt-[env(safe-area-inset-top)] backdrop-blur-md"
      >
        {banner}
        <div className="rail h-bar flex items-center gap-3 sm:gap-4">
          <a
            href={PAGES.home}
            onClick={onClick}
            className="shrink-0 rounded-control"
            aria-label="AlphaDesk — home"
          >
            <Brand size="sm" />
          </a>
          <Connection state={connection} />
          <Nav viewer={viewer} />

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5">
            <InvitePill />
            <BrokerPill viewer={viewer} />
            <InboxBell notifications={notifications} unread={unread} />
            <AccountLink viewer={viewer} />
          </div>
        </div>
      </header>

      <BrokerBanner />

      {/* The rail column stays held on every page, so Trades' columns never shift. */}
      <main className="flex-1 pb-[var(--tab-bar)]">
        <div className="rail flex items-stretch">
          <div className="py-page min-w-0 flex-1">{children}</div>
          {rail ?? (
            <InboxRail
              notifications={notifications}
              unread={unread}
              drawn={path !== PAGES.notifications}
            />
          )}
        </div>
      </main>

      <TabBar viewer={viewer} unread={unread} />
    </div>
  );
}
